<template>
  <div>
    <v-hover v-slot="{ isHovering, props }">
      <v-card
        elevation="0"
        :loading="loading"
        tile
        v-bind="props"
        :disabled="loading"
      >
        <!-- Custom loader with color -->
        <template v-slot:loader="{ isActive }">
          <v-progress-linear
            :active="isActive"
            color="primary"
            height="3"
            indeterminate
          ></v-progress-linear>
        </template>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          class="mb-2"
          density="compact"
          variant="tonal"
        >
          {{ error }}
        </v-alert>

        <!-- Main Content -->
        <div v-else-if="hasData">
          <h2 class="text-h6 text-primary mb-8">
            <v-icon left color="secondary">mdi-weather-cloudy</v-icon>
            Barometer
          </h2>
          <!-- Display gauge and value -->
          <v-row no-gutters justify="center">
            <v-col cols="12">
              <gauge :percentage="scaledValue" />
            </v-col>
          </v-row>
        </div>

        <!-- No Data Message -->
        <div v-else-if="!loading" class="text-center pa-4">
          <v-icon color="grey" size="large">mdi-weather-cloudy</v-icon>
          <p class="text-grey mt-2">No pressure data available</p>
        </div>

        <!-- Hover Overlay -->
        <v-overlay
          :model-value="isHovering && !error && hasData"
          @click="show = true"
          class="align-center justify-center"
          scrim="rgba(138, 177, 211, 0.4)"
          contained
          style="cursor: pointer"
        >
          <v-btn variant="flat" color="secondary">See more info</v-btn>
        </v-overlay>
      </v-card>
    </v-hover>

    <!-- Detailed View Overlay -->
    <v-overlay
      v-model="show"
      :opacity="0.6"
      color="primary"
      z-index="7"
      class="d-flex align-center justify-center overlay-primary"
      scrollable
    >
      <v-card light class="chart-container" :style="containerStyle">
        <v-container fluid class="d-flex flex-column h-100">
          <!-- Header Row -->
          <div class="header-container">
            <v-btn
              color="primary"
              @click="closeOverlay"
              icon="mdi-close"
              size="small"
              variant="flat"
              class="close-btn"
            ></v-btn>

            <div class="timestamp-container" v-if="latestTimestamp">
              <timestamp-status
                :latestTimestamp="latestTimestamp"
              ></timestamp-status>
            </div>
          </div>

          <!-- Error Alert -->
          <v-alert v-if="error" type="error" class="ma-4" variant="tonal">
            {{ error }}
          </v-alert>

          <!-- Pressure Chart -->
          <v-row class="charts-row flex-grow-1">
            <v-col cols="12" class="d-flex">
              <div class="chart-wrapper">
                <!-- Plotly Chart -->
                <PlotCustom
                  v-if="hasData && data.length > 0"
                  :key="chartKey"
                  :data="data"
                  :layout="layout"
                  :config="{ responsive: true, displayModeBar: false }"
                  class="h-100 w-100 plotly-chart"
                ></PlotCustom>

                <!-- No Data Message -->
                <div v-else-if="!loading" class="no-data-message">
                  <v-icon color="grey" size="large">mdi-weather-cloudy</v-icon>
                  <p class="text-grey mt-2">No pressure data available.</p>
                </div>

                <!-- Loading Overlay -->
                <div v-if="loading" class="loading-overlay">
                  <v-progress-linear
                    indeterminate
                    color="primary"
                  ></v-progress-linear>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import PlotCustom from "@/components/PlotCustom.vue";
import timestampStatus from "@/components/TimestampStatus.vue";
import generateShapes from "@/plugins/verticalIntervals";
import { plotColours } from "@/plugins/helper.js";
import Gauge from "@/components/GaugeComponent.vue";
import { useDisplay } from "vuetify";
import { fetchPressureData } from "@/Service/pressureService";

export default {
  name: "BarometerView",
  components: {
    PlotCustom,
    timestampStatus,
    Gauge,
  },
  emits: ["latestDate"],

  props: {
    site: {
      type: String,
      default: "Tasman_Bay 119",
      required: true,
    },
  },

  data() {
    return {
      show: false,
      loading: true,
      error: null,
      data: [],
      highContrast: true,
      refresher: null,
      interval: 300000,
      hasData: false,
      isComponentAlive: true,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      chartKey: 0,
      orientation:
        window.innerWidth > window.innerHeight ? "landscape" : "portrait",
    };
  },

  setup() {
    const display = useDisplay();
    return { display };
  },

  computed: {
    containerStyle() {
      const padding = 32;
      const maxWidth = Math.min(1200, this.windowWidth - padding);
      const maxHeight = this.windowHeight - padding;

      return {
        maxWidth: `${maxWidth}px`,
        maxHeight: `${maxHeight}px`,
        width: "100%",
        height: "100%",
      };
    },

    latestValue() {
      if (!this.hasData || !this.data?.[0]?.y?.length) return null;
      const value = this.data[0].y[this.data[0].y.length - 1];
      return value != null ? value.toFixed(1) : null;
    },

    latestTimestamp() {
      if (!this.hasData || !this.data?.[0]?.x?.length) return null;
      const timestamps = this.data[0].x;
      const timestamp = timestamps[timestamps.length - 1] ?? null;
      return timestamp ? new Date(timestamp) : null;
    },

    scaledValue() {
      if (this.hasData && this.data[0]?.y?.length) {
        const val = this.data[0].y[this.data[0].y.length - 1];
        const scaled = ((val - 950) / 134) * 100;
        return { scaled, val };
      } else {
        return { scaled: 0, val: 0 };
      }
    },

    layout() {
      const verticalIntervals = [
        {
          label: "Dry",
          labelLocation: 1025,
          upper: 2025,
          lower: 1025,
          color: plotColours[this.highContrast ? "contrast" : "default"][4],
        },
        {
          label: "Fair",
          labelLocation: 1010,
          upper: 1025,
          lower: 1010,
          color: plotColours[this.highContrast ? "contrast" : "default"][3],
        },
        {
          label: "Change",
          labelLocation: 990,
          upper: 1010,
          lower: 990,
          color: plotColours[this.highContrast ? "contrast" : "default"][2],
        },
        {
          label: "Rain",
          labelLocation: 975,
          upper: 990,
          lower: 975,
          color: plotColours[this.highContrast ? "contrast" : "default"][1],
        },
        {
          label: "Stormy",
          labelLocation: 960,
          upper: 975,
          lower: 960,
          color: plotColours[this.highContrast ? "contrast" : "default"][0],
        },
      ];

      const [annotations, shapes] = generateShapes(verticalIntervals);

      const isLandscape = this.windowWidth > this.windowHeight;
      const graphHeight = isLandscape
        ? this.windowHeight * 0.75
        : this.windowHeight * 0.5;

      return {
        shapes,
        annotations,
        autoresize: true,
        autosize: true,
        height: graphHeight,
        showlegend: false,
        margin: {
          b: 40,
          l: 50,
          r: 20,
          t: 20,
        },
        yaxis: {
          showgrid: false,
          title: "Pressure [mBar]",
          ticks: "inside",
          range: [960, 1050],
          automargin: true,
        },
        xaxis: {
          automargin: true,
        },
      };
    },
  },

  methods: {
    cleanup() {
      if (this.refresher) {
        clearTimeout(this.refresher);
        this.refresher = null;
      }
    },

    closeOverlay() {
      this.show = false;
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;

      const newOrientation =
        this.windowWidth > this.windowHeight ? "landscape" : "portrait";
      if (newOrientation !== this.orientation) {
        this.orientation = newOrientation;
        this.chartKey++;

        setTimeout(() => {
          this.chartKey++;
        }, 150);
      }
    },

  async refreshData() {
  this.loading = true;
  this.error = null;

  try {
    const response = await fetchPressureData(this.site, 0);

    if (!response.success) {
      this.error = response.error;
      this.data = [];
      this.hasData = false;
      return;
    }

    if (response.noDataAvailable) {
      this.handleNoData();
      return;
    }

    if (this.isComponentAlive) {
      const sortedData = response.time
        .map((time, index) => ({
          time: new Date(time),
          value: response.values[index],
        }))
        .sort((a, b) => a.time - b.time)
        .filter((d) => d.value !== null);  // Filter out null values

      // Check if we have any valid data points after filtering
      if (sortedData.length === 0) {
        this.handleNoData();
        return;
      }

      this.data = [
        {
          x: sortedData.map((d) => d.time),
          y: sortedData.map((d) => d.value),
          type: "scatter",
          mode: "lines",
          line: { width: 2, color: plotColours.default[4] },
        },
      ];

      this.hasData = this.data[0].y.some(value => value !== null);
      if (!this.hasData) {
        this.handleNoData();
        return;
      }

      this.$emit("latestDate", this.latestTimestamp);
    }
  } catch (error) {
    this.error = "Unable to fetch pressure data";
    this.data = [];
    this.hasData = false;
  } finally {
    this.loading = false;
    if (this.isComponentAlive) {
      this.refresher = setTimeout(() => this.refreshData(), this.interval);
    }
  }
},

    handleNoData() {
      this.clearData();
    },

    clearData() {
      this.hasData = false;
      this.data = [];
    },
  },

  watch: {
    site: {
      immediate: true,
      handler() {
        this.cleanup();
        this.show = false;
        this.refreshData();
      },
    },
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  beforeUnmount() {
    this.isComponentAlive = false;
    this.cleanup();
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>
<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
  min-width: 90vw;
  background: white;
  border-radius: 4px;

  display: flex;
  flex-direction: column;
}

.header-container {
  position: relative;
  width: 100%;
  min-height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  padding: 8px;
  z-index: 2;
}

.timestamp-container {
  text-align: center;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
  width: 64px !important;
  height: 40px !important;
  border-radius: 4px !important;
  padding: 0 !important;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
  min-height: 300px;
  position: relative;
  flex-grow: 1;
}

.plotly-chart {
  width: 100% !important;
  height: 100% !important;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.charts-row {
  flex: 1;
  min-height: 0;
  margin: 0;
  height: 100%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

/* Desktop large screens */
@media (min-width: 2560px) {
  .chart-container {
    min-width: 80vw;
  }
}

@media (min-width: 1920px) {
  .chart-container {
    width: 75vw;
  }
}

@media (max-width: 1919px) {
  .chart-container {
    width: 85vw;
  }
}

/* Mobile and small screens */
@media (max-width: 600px) {
  .charts-row {
    flex-direction: column;
    gap: 20px;
  }

  .header-container {
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
  }

  .chart-container {
    margin: 4px;
    overflow-y: auto;
    overflow-x: auto;
    min-width: 100vw;
  }

  .chart-wrapper {
    min-height: 250px;
  }

  .close-btn {
    position: relative;
  }

  .header-container {
    min-height: 40px;
    padding: 4px;
  }
}

/* Landscape mode adjustments */
@media (max-height: 500px) and (orientation: landscape) {
  .chart-container {
    max-height: 100vh;
    min-width: 100vw;
    margin: 0;
  }

  .chart-wrapper {
    min-height: 200px;
  }

  .close-btn {
    top: 4px;
    right: 4px;
  }

  .header-container {
    min-height: 40px;
    padding: 4px;
  }
}

/* Ensure proper display on very small screens */
@media (max-width: 320px) {
  .chart-container {
    margin: 0;
  }

  .chart-wrapper {
    min-height: 200px;
  }
}

.error-message {
  margin-top: 20px;
}

/* Additional styling */
.overlay-primary {
  position: fixed;
  inset: 0;
}

.temperature-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Loading states */
.v-progress-linear {
  margin: 0;
}

.v-progress-circular {
  margin: auto;
}
</style>
